import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { requestBlob, requestJson } from '../../lib/util';
import { usePxtraCardEnabled } from '../../hooks/usePxtraCardEnabled';
import { useToday } from '../../lib/components/compound/pxtraCardWallet/useToday';
import type { WalletPeriod } from '../../lib/components/compound/pxtraCardWallet/walletPeriod';
import { WalletSummary, WalletTransactions, WalletUpcomingPayments } from './types';
import { isServiceUnreachable, shouldRetryCardQuery } from './walletError';
import {
  keepForCompany,
  latestCachedSummary,
  walletDay,
  walletKey,
  walletSummaryKey,
  walletTransactionsKey,
  walletUpcomingKey
} from './walletKeys';

/** The legacy backend forwards these to the new backend, which owns the subledger. */
const walletPath = (companyId: string) => `/api/v2/companies/${companyId}/pxtra-card/wallet`;

/** The pool moves only when a webhook lands; a minute is fresh enough. */
const STALE_MS = 60_000;
// Keeps yesterday's summary cached after midnight moves the key on, so a refetch that
// fails after midnight still has it to fall back on.
const SUMMARY_GC_MS = 36 * 60 * 60 * 1000;

export const useWallet = (companyId: string, options = {}) => {
  const { isCardEnabled } = usePxtraCardEnabled();
  const day = walletDay(useToday());
  const queryClient = useQueryClient();

  const query = useQuery<WalletSummary>({
    queryKey: walletSummaryKey(companyId, day),
    queryFn: () => requestJson<WalletSummary>({ path: walletPath(companyId) }),
    enabled: !!companyId && isCardEnabled,
    staleTime: STALE_MS,
    gcTime: SUMMARY_GC_MS,
    retry: shouldRetryCardQuery,
    // The previous day's answer bridges midnight, but never another company's.
    placeholderData: keepForCompany(companyId),
    ...options
  });

  // A fault or a rejection must show; only an unreachable service falls back to an earlier
  // answer, and the page marks that answer as stale while the query stays in error. A retry
  // of a key that never answered resets the query to pending, so the earlier answer also
  // stays on screen while that fetch runs.
  if (query.data) return query;
  const fallsBack = query.isError ? isServiceUnreachable(query.error) : query.isFetching;
  if (!fallsBack) return query;
  const cached = latestCachedSummary<WalletSummary>(queryClient, companyId);
  return cached ? { ...query, ...cached } : query;
};

export const useUpcomingPayments = (companyId: string, period: WalletPeriod, today: Date) =>
  useQuery<WalletUpcomingPayments>({
    queryKey: walletUpcomingKey(companyId, period.from, period.until, walletDay(today)),
    queryFn: () =>
      requestJson<WalletUpcomingPayments>({
        path: `${walletPath(companyId)}/upcoming-payments?from=${period.from}&until=${period.until}`
      }),
    enabled: !!companyId,
    staleTime: STALE_MS,
    retry: shouldRetryCardQuery,
    // The previous period's numbers bridge a period change, but never another company's.
    placeholderData: keepForCompany(companyId)
  });

export const useWalletTransactions = (companyId: string, year: number, enabled = true) =>
  useQuery<WalletTransactions>({
    queryKey: walletTransactionsKey(companyId, year),
    queryFn: () =>
      requestJson<WalletTransactions>({
        path: `${walletPath(companyId)}/transactions?year=${year}`
      }),
    enabled: !!companyId && enabled,
    staleTime: STALE_MS,
    retry: shouldRetryCardQuery
  });

export type WalletCsvScope =
  | { year: number; month?: number }
  /** `date` (`YYYY-MM-DD`, Berlin) only names the file. */
  | { transactionId: string; date?: string };

export const useDownloadWalletCsv = (companyId: string) =>
  useMutation({
    mutationFn: (scope: WalletCsvScope) => {
      if ('transactionId' in scope) {
        return requestBlob({
          path: `${walletPath(companyId)}/transactions/${scope.transactionId}?format=csv`
        });
      }
      const month = scope.month === undefined ? '' : `&month=${scope.month}`;
      return requestBlob({
        path: `${walletPath(companyId)}/transactions?year=${scope.year}${month}&format=csv`
      });
    }
  });

export const invalidateWallet = (queryClient: QueryClient, companyId: string) =>
  queryClient.invalidateQueries({ queryKey: walletKey(companyId) });
